import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import { useForm, UseFormGetValues, UseFormSetError } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@store';
import isEqual from 'lodash.isequal';
import {
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  AllowedCategory,
  ApplicationAgreement,
  ApplicationDocument,
  CorporateFormValues,
  ExpectedFundsOriginsFormValues,
  NewApplicationChildrenInfo,
  NewApplicationCorporateInfo,
  NewApplicationCorporateSteps,
  NewApplicationStatus,
  NewApplicationsValidatorResponse,
} from '../../types';
import { updateApplicationStepStatuses } from '../helpers/update-application-step-statuses';
import {
  ApplicationScheme,
  CustomerScoreUpdateStatus,
  OngoingCheckingStatus,
  OngoingMonitring,
} from '../../../common/types';
import { CorporateQuestionnarieFormValues } from '../../../corporate/types';
import {
  useLazyGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersApplicationSchemesQuery,
  useLazyGetCustomersEmployeesListQuery,
} from '../../../../store/dictionaries/services';
import {
  useCreateCorporateApplicationMutation,
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationLastCalculatingScoreQuery,
  useLazyGetCorporateNewApplicationDocumentsQuery,
  useLazyGetNewApplicationCorporateInfoQuery,
  useLazyGetNewApplicationCorporateOrganizationQuery,
  useLazyGetNewApplicationDefaultDocumentCategoriesQuery,
  useLazyGetNewApplicationsValidatorsQuery,
  useUpdateCorporateApplicationMutation,
  useUpdateCorporateApplicationStatusMutation,
  useUpdateCorporateQuestionnarieMutation,
} from '../../store/services';
import getCorporateAppicationBody from '../helpers/get-corporate-application-body';
import getCorporateQuestionnarieBody from '../helpers/get-questionnarie-body';
import { clearCofAndMRState, updateStepsStatus } from '../store/slices';

// eslint-disable-next-line no-useless-escape
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;

const schema = {
  type: 'object',
  properties: {},
  required: [],
};

const useApplication = ({
  documents,
  agreements,
  mainRepresentativeApplications,
  companyOfficialsApplications,
  ongoingMonitoring,
  ongoingCheckingStatus,
  applicationStepsStatus,
  allowedDocumentsCategories,
  newApplicationInfo,
  questionnarieFields,
  accountsCurrenciesValues,
  expectedFundsOriginsValues,
  accountsFlowsValues,
  applicationId,
  questionnarieGetValues,
  questionnarieSetError,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  activePermissions,
  prevApplicationId,
  validators,
  applicationSchemes,
  amlUpdateStatus,
  defaultQuestionnarieFormValues,
}: {
  documents?: ApplicationDocument[];
  agreements?: ApplicationAgreement[];
  mainRepresentativeApplications?: NewApplicationChildrenInfo[];
  companyOfficialsApplications?: NewApplicationChildrenInfo[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  applicationStepsStatus?: NewApplicationCorporateSteps;
  allowedDocumentsCategories?: AllowedCategory[];
  newApplicationInfo?: NewApplicationCorporateInfo;
  questionnarieFields?: CorporateQuestionnarieFormValues;
  accountsCurrenciesValues?: AccountsCurrenciesFormValues[];
  expectedFundsOriginsValues?: ExpectedFundsOriginsFormValues[];
  accountsFlowsValues?: AccountsFlowsFormValues[];
  applicationId?: string;
  questionnarieGetValues: UseFormGetValues<CorporateQuestionnarieFormValues>;
  questionnarieSetError: UseFormSetError<CorporateQuestionnarieFormValues>;
  onSaveAccountsCurrencies: () => void;
  onSaveAccountsFlows: () => void;
  onSaveExpectedFundsOrigins: () => void;
  activePermissions?: PermissionEnum[];
  prevApplicationId?: string;
  validators?: NewApplicationsValidatorResponse;
  applicationSchemes?: ApplicationScheme[];
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  defaultQuestionnarieFormValues?: CorporateQuestionnarieFormValues;
}) => {
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [initialFields, setInitialFields] = useState({});
  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [statusChangeError, setStatusChangeError] = useState('');
  const [link, setLink] = useState('');
  const [customersSuppliersStepEnabled, setCustomersSuppliersStepEnbaled] =
    useState(false);
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateQuestionnarieError, setUpdateQuestionnarieError] = useState('');

  const dispatch = useAppDispatch();

  const {
    riskScoreRanges,
    customersTransactionTypes,
    sicCodesDictionaries,
    deliveryChannelsCorporate,
    productsCorporate,
    sourcesOfFunds,
    clientsActivities,
    monthlyTurnoverCorporate,
    numberOfIncomingTransactionsCorporate,
    numberOfOutgoingTransactionsCorporate,
    valueOfIncomingTransactionsCorporate,
    valueOfOutgoingTransactionsCorporate,
    maxValueOfIncomingPaymentCorporate,
    maxValueOfOutgoingPaymentCorporate,
  } = useAppSelector((state) => state.customersDictionaries);

  const {
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<CorporateFormValues>({
    resolver: ajvResolver(formattedSchema),
  });

  const fields = watch();

  const websiteLink = watch('websiteLink');
  const prevWebsiteLink = usePrevious(websiteLink);

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    documents,
    agreements,
    mainRepresentativeApplications,
    companyOfficialsApplications,
    ongoingMonitoring,
    ongoingCheckingStatus,
    documentCategories: allowedDocumentsCategories,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
  });

  const prevStepsStatus = usePrevious(stepsStatus);

  const [getDictionaries] = useLazyGetCustomersInfoDictionariesQuery();
  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationCorporateInfoQuery();
  const [getApplicationOrganization] =
    useLazyGetNewApplicationCorporateOrganizationQuery();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [create] = useCreateCorporateApplicationMutation();
  const [update, { isLoading: requestInProgress }] =
    useUpdateCorporateApplicationMutation();
  const [createNewCorporateCustomer, { isLoading: createInProgress }] =
    useUpdateCorporateApplicationStatusMutation();
  const [getDocuments] = useLazyGetCorporateNewApplicationDocumentsQuery();
  const [
    getApplicationAgreements,
    { isLoading: agreementsLoading, isFetching: agreementsFetching },
  ] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();

  const [getDefaultDocumentCategories] =
    useLazyGetNewApplicationDefaultDocumentCategoriesQuery();
  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();
  const [
    updateCorporateQuestionnarie,
    { isLoading: questionnarieUpdateInProgress },
  ] = useUpdateCorporateQuestionnarieMutation();
  const [getApplicationLastCalculaingScore] =
    useLazyGetApplicationLastCalculatingScoreQuery();

  const getApplicationDocuments = (id: string) => {
    getDocuments(id);
  };

  const onApplicationCOFSubmit = ({
    id,
    body,
  }: {
    id: string;
    body: CorporateFormValues;
  }) => {
    update({
      body,
      id,
    });
  };

  const onSubmit = handleSubmit(async (values) => {
    setUpdateError('');
    setUpdateQuestionnarieError('');

    const body = getCorporateAppicationBody(values);

    if (!body.websiteLink) {
      body.websiteLink = '';
    }

    if (applicationId) {
      const response = (await update({
        body,
        id: applicationId,
      })) as { error?: ResponseError };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setUpdateError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.accountantDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.auditedByExternalComplianceActive ||
          newApplicationInfo?.applicationScheme
            ?.changesInManagementOrOwnershipActive ||
          newApplicationInfo?.applicationScheme
            ?.complianceConsultantDetailsActive ||
          newApplicationInfo?.applicationScheme?.fcaNumberActive ||
          newApplicationInfo?.applicationScheme?.hmrcMlrNumberActive ||
          newApplicationInfo?.applicationScheme
            ?.hmrcRegistrationExpiryDateActive ||
          newApplicationInfo?.applicationScheme?.icoNumberActive ||
          newApplicationInfo?.applicationScheme?.itProviderDetailsActive ||
          newApplicationInfo?.applicationScheme?.mlroDetailsActive ||
          newApplicationInfo?.applicationScheme?.numberOfAgentsActive ||
          newApplicationInfo?.applicationScheme?.numberOfBranchesActive ||
          newApplicationInfo?.applicationScheme
            ?.onboardCorporateClientsActive ||
          newApplicationInfo?.applicationScheme?.onboardMsbActive ||
          newApplicationInfo?.applicationScheme?.paymentsForCharitiesActive ||
          newApplicationInfo?.applicationScheme?.payoutPartnerDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.regulatoryInvestigationActive ||
          newApplicationInfo?.applicationScheme?.screeningActive ||
          newApplicationInfo?.applicationScheme?.transactionMonitoringActive ||
          newApplicationInfo?.applicationScheme?.typeOfBusinessLicenceActive ||
          newApplicationInfo?.applicationScheme?.visitedByAuthoritiesActive ||
          newApplicationInfo?.applicationScheme
            ?.workingWithAnyOtherIPSPActive ||
          newApplicationInfo?.applicationScheme?.id ===
            'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieValues = questionnarieGetValues();

        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues,
          scheme: newApplicationInfo?.applicationScheme,
        });

        const questionnarieResponse = (await updateCorporateQuestionnarie({
          id: newApplicationInfo?.id,
          body: questionnarieBody,
        })) as { error?: ResponseError };

        if (questionnarieResponse?.error) {
          responseErrorsResolver({
            errors: questionnarieResponse.error,
            setError: questionnarieSetError,
            setGlobalError: setUpdateQuestionnarieError,
          });
        }

        if (
          newApplicationInfo?.applicationScheme?.id ===
          'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
        ) {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    } else {
      create(body);
    }
  });

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);
      getDefaultDocumentCategories(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
        )
      ) {
        getDocuments(applicationId);
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createNewCorporateCustomer({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 8000);
    }
  };

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      dispatch(clearCofAndMRState());

      setTimeout(() => {
        getInfoAction();
      }, 10);
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    let nextSchema: any = schema;

    if (websiteLink) {
      nextSchema = {
        ...schema,
        properties: {
          ...schema.properties,
          websiteLink: {
            type: 'string',
            regexp: urlRegex.toString(),
            errorMessage: 'Must be a URL address',
          },
        },
      };
    } else if (!websiteLink && prevWebsiteLink) {
      nextSchema = schema;
    }

    setFormattedSchema(nextSchema);
  }, [websiteLink, prevWebsiteLink]);

  useEffect(() => {
    if (errors && Object.keys(errors).length) {
      setLink(`#${Object.keys(errors)[0]}`);
    }
  }, [errors]);

  useEffect(() => {
    if (link && linkRef && linkRef.current) {
      (linkRef.current as { click: VoidFunction }).click();

      setLink('');
    }
  }, [link, linkRef]);

  useEffect(() => {
    if (applicationId) {
      if (
        !riskScoreRanges ||
        !customersTransactionTypes ||
        !sicCodesDictionaries ||
        !deliveryChannelsCorporate ||
        !productsCorporate ||
        !sourcesOfFunds ||
        !clientsActivities ||
        !monthlyTurnoverCorporate ||
        !numberOfIncomingTransactionsCorporate ||
        !numberOfOutgoingTransactionsCorporate ||
        !valueOfIncomingTransactionsCorporate ||
        !valueOfOutgoingTransactionsCorporate ||
        !maxValueOfIncomingPaymentCorporate ||
        !maxValueOfOutgoingPaymentCorporate
      ) {
        setTimeout(() => {
          getDictionaries();
        }, 10);
      }
    }
  }, [
    applicationId,
    riskScoreRanges,
    customersTransactionTypes,
    sicCodesDictionaries,
    deliveryChannelsCorporate,
    productsCorporate,
    sourcesOfFunds,
    clientsActivities,
    monthlyTurnoverCorporate,
    numberOfIncomingTransactionsCorporate,
    numberOfOutgoingTransactionsCorporate,
    valueOfIncomingTransactionsCorporate,
    valueOfOutgoingTransactionsCorporate,
    maxValueOfIncomingPaymentCorporate,
    maxValueOfOutgoingPaymentCorporate,
  ]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [activePermissions, applicationSchemes]);

  useEffect(() => {
    if (newApplicationInfo) {
      if (newApplicationInfo?.applicationScheme) {
        setCustomersSuppliersStepEnbaled(
          Boolean(
            newApplicationInfo?.applicationScheme?.customersEnabled ||
            newApplicationInfo?.applicationScheme?.suppliersEnabled ||
            (newApplicationInfo?.applicationScheme?.customersEnabled === null &&
              newApplicationInfo?.applicationScheme?.suppliersEnabled === null),
          ),
        );
      } else {
        setCustomersSuppliersStepEnbaled(true);
      }
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationLastCalculaingScore(applicationId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (newApplicationInfo) {
      let amlCtfComplianceProgram: string | null = null;
      let requiresBusinessLicense: string | null = null;

      if (newApplicationInfo?.amlCtfComplianceProgram !== null) {
        if (newApplicationInfo?.amlCtfComplianceProgram) {
          amlCtfComplianceProgram = 'yes';
        } else {
          amlCtfComplianceProgram = 'no';
        }
      }

      if (newApplicationInfo?.requiresBusinessLicense !== null) {
        if (newApplicationInfo?.requiresBusinessLicense) {
          requiresBusinessLicense = 'yes';
        } else {
          requiresBusinessLicense = 'no';
        }
      }

      const formValues = {
        countryOfIncorporationId:
          newApplicationInfo.countryOfIncorporationId || '',
        companyName: newApplicationInfo.companyName || '',
        registeredCountryId: newApplicationInfo.registeredCountryId || '',
        registeredCity: newApplicationInfo.registeredCity || '',
        registeredPostalCode: newApplicationInfo.registeredPostalCode || '',
        registeredAddress1: newApplicationInfo.registeredAddress1 || '',
        registeredAddress2: newApplicationInfo.registeredAddress2 || '',
        businessCountryId: newApplicationInfo.businessCountryId || '',
        businessCity: newApplicationInfo.businessCity || '',
        businessPostalCode: newApplicationInfo.businessPostalCode || '',
        businessAddress1: newApplicationInfo.businessAddress1 || '',
        businessAddress2: newApplicationInfo.businessAddress2 || '',
        correspondenceCountryId:
          newApplicationInfo.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo.correspondencePostalCode || '',
        correspondenceAddress1: newApplicationInfo.correspondenceAddress1 || '',
        correspondenceAddress2: newApplicationInfo.correspondenceAddress2 || '',
        dateOfIncorporation: newApplicationInfo.dateOfIncorporation || '',
        companyNumber: newApplicationInfo.companyNumber || '',
        sicCodes: newApplicationInfo?.sicCodes || [],
        businessLicense: newApplicationInfo.businessLicense || '',
        websiteLink: newApplicationInfo.websiteLink || '',
        countriesOfInterestIds: newApplicationInfo.countriesOfInterestIds || [],
        businessDescription: newApplicationInfo.businessDescription || '',
        businessAddressSameAsRegisteredAddress:
          newApplicationInfo.businessAddressSameAsRegisteredAddress || false,
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo.correspondenceAddressSameAsRegisteredAddress ||
          false,
        monthlyTurnoverRangeId: newApplicationInfo.monthlyTurnoverRangeId || '',
        monthlyNumberIncomingTransactionsRangeId:
          newApplicationInfo.monthlyNumberIncomingTransactionsRangeId || '',
        monthlyNumberOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyNumberOutgoingTransactionsRangeId || '',
        monthlyValueIncomingTransactionsRangeId:
          newApplicationInfo.monthlyValueIncomingTransactionsRangeId || '',
        monthlyValueOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyValueOutgoingTransactionsRangeId || '',
        monthlyMaxValueIncomingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueIncomingPaymentRangeId || '',
        monthlyMaxValueOutgoingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueOutgoingPaymentRangeId || '',
        productIds:
          newApplicationInfo?.products?.edges.map((item) => item.node.id) || [],
        suppliers:
          newApplicationInfo?.suppliers?.map((item) => ({
            id: item.id,
            name: item.name,
            monthlyTurnoverRangeId: item.monthlyTurnoverRangeId,
          })) || [],
        customers:
          newApplicationInfo?.customers?.map((item) => ({
            id: item.id,
            name: item.name,
            monthlyTurnoverRangeId: item.monthlyTurnoverRangeId,
          })) || [],

        deliveryChannelId: newApplicationInfo?.deliveryChannelId || '',
        reviewDuplicatesComment:
          newApplicationInfo.reviewDuplicatesComment || '',
        reviewedDuplicates: newApplicationInfo.reviewedDuplicates || [],
        vatNumer: newApplicationInfo?.vatNumber || '',
        amlCtfComplianceProgram,
        requiresBusinessLicense,
        requiredCurrenciesCryptoId:
          newApplicationInfo?.requiredCurrenciesCryptoId || [],
        purposeOfAccount: newApplicationInfo?.purposeOfAccount || '',
        expectedTypeOfClients: newApplicationInfo?.expectedTypeOfClients || [],
      };

      setDefaultFormValues({
        ...formValues,
        companyOfficial:
          companyOfficialsApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
        mainRepresentative:
          mainRepresentativeApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
      });
      reset({
        ...formValues,
        companyOfficial:
          companyOfficialsApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
        mainRepresentative:
          mainRepresentativeApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
      });
      setInitialFields(formValues);

      if (
        (!newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive) ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive === null)) &&
        !agreements
      ) {
        getApplicationAgreements(newApplicationInfo.id);
      }
    }
  }, [reset, dispatch, newApplicationInfo]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return {
    userCreated,
    isLoading,
    isFetching,
    agreementsLoading,
    agreementsFetching,
    applicationLoadErrorVisible,
    getApplicationDocuments,
    setStatusChangeError,
    setSuccessMessageVisible,
    requestInProgress,
    questionnarieUpdateInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    setError,
    clearErrors,
    customersSuppliersStepEnabled,
    control,
    errors,
    setValue,
    initialFields,
    onApplicationCOFSubmit,
    setDefaultFormValues,
    getApplicationChat,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    successMessageVisible,
    link,
    linkRef,
    createInProgress,
    onSubmit,
    onCreateCustomer,
    updateError,
    updateQuestionnarieError,
  };
};

export default useApplication;
